import type { SceneHost } from "../SceneHost";
import { isBoardEditorChrome } from "./boardWidgetChrome";
import { toggleBoardCursorClass } from "../boardCursorSurfaces";

export type HandToolBinding = {
  destroy: () => void;
};

type PanState = {
  pointerId: number;
  x: number;
  y: number;
};

/**
 * Инструмент «Рука» (H): перетаскивание сдвигает холст, объекты не перехватывают клики.
 */
export function bindHandTool(host: SceneHost): HandToolBinding {
  const stage = host.stage;
  const sceneLayer = stage.querySelector<HTMLElement>(".wb-board-scene-layer");
  const prevLayerPe = sceneLayer?.style.pointerEvents ?? "";

  if (sceneLayer) {
    sceneLayer.style.pointerEvents = "none";
  }
  toggleBoardCursorClass(host, "is-hand-dragging-ready", true);

  let pan: PanState | null = null;

  function endPan(ev?: PointerEvent) {
    if (!pan) return;
    if (ev && pan.pointerId === ev.pointerId) {
      try {
        host.hitSurface.releasePointerCapture(ev.pointerId);
      } catch {
        /* ignore */
      }
    }
    pan = null;
    toggleBoardCursorClass(host, "is-hand-dragging", false);
  }

  function onPointerDown(ev: PointerEvent) {
    if (host.getActiveTool() !== "hand") return;
    if (isBoardEditorChrome(ev.target)) return;
    // Панорама камеры разрешена и во время ввода текста (окно не закрываем).
    if (ev.button !== 0) return;
    ev.preventDefault();
    ev.stopPropagation();

    // Останавливает coast от предыдущего жеста (panBy(0,0) — только interrupt).
    host.panBy(0, 0);
    pan = { pointerId: ev.pointerId, x: ev.clientX, y: ev.clientY };
    toggleBoardCursorClass(host, "is-hand-dragging", true);
    try {
      host.hitSurface.setPointerCapture(ev.pointerId);
    } catch {
      /* ignore */
    }
  }

  function onPointerMove(ev: PointerEvent) {
    if (!pan || pan.pointerId !== ev.pointerId) return;
    const dx = ev.clientX - pan.x;
    const dy = ev.clientY - pan.y;
    pan = { pointerId: pan.pointerId, x: ev.clientX, y: ev.clientY };
    if (dx !== 0 || dy !== 0) {
      host.panBy(dx, dy);
    }
  }

  function onPointerUp(ev: PointerEvent) {
    if (!pan || pan.pointerId !== ev.pointerId) return;
    endPan(ev);
    host.endPanGesture?.();
  }

  function onViewportGestureStart() {
    endPan();
  }

  host.hitSurface.addEventListener("pointerdown", onPointerDown);
  host.hitSurface.addEventListener("pointermove", onPointerMove);
  host.hitSurface.addEventListener("pointerup", onPointerUp);
  host.hitSurface.addEventListener("pointercancel", onPointerUp);
  window.addEventListener("wb:viewport-gesture-start", onViewportGestureStart);

  return {
    destroy() {
      endPan();
      host.hitSurface.removeEventListener("pointerdown", onPointerDown);
      host.hitSurface.removeEventListener("pointermove", onPointerMove);
      host.hitSurface.removeEventListener("pointerup", onPointerUp);
      host.hitSurface.removeEventListener("pointercancel", onPointerUp);
      window.removeEventListener("wb:viewport-gesture-start", onViewportGestureStart);
      toggleBoardCursorClass(host, "is-hand-dragging", false);
      toggleBoardCursorClass(host, "is-hand-dragging-ready", false);
      if (sceneLayer) {
        sceneLayer.style.pointerEvents = prevLayerPe;
      }
    },
  };
}
